/**
 * DocumentsTab — the record page's filing cabinet (D8 v1, migration
 * 0131). The coming-soon frame comes alive: upload, list, open (browser
 * preview for previewable types), download, version, remove.
 *
 * Open (document-preview follow-on): shown only for previewable types
 * (isPreviewable — pdf, png, jpeg, plain text; docx is Download-only).
 * The click opens a tab synchronously (popup-blocker-safe), fetches the
 * inline grant, and points the tab at it; a failed grant closes the tab
 * and shows the ruled retry copy.
 *
 * Picked or dropped files open a box with one row per file (a pick
 * takes several, a later pick or drop adds to the list): a Name box
 * (the file name without its ending, which is kept on what is sent) and
 * a required File as that starts on "Choose a kind", with Other last and
 * never chosen for you. "Upload document" ("Upload documents" for
 * several) sends each row with a kind as its own upload; a row with no
 * kind sends nothing, says so under its File as, and the first such row
 * takes focus. Each upload is the three-step handshake
 * (documentsApi.uploadDocument): declare -> the browser PUTs the bytes
 * straight to storage -> complete. A row shows its progress plainly
 * ("Uploading…"), leaves the list when its file lands, and on a failure
 * stays with the ruled retry treatment ("That file didn't upload. Add it
 * again, ..."), so retry is one click and re-sends its name and kind. A
 * failed file never stops the others.
 *
 * Replace = a re-upload carrying the replaced row's id (the version
 * chain); the old version leaves the list when the new one lands and
 * the new row carries the quiet "Replaces the earlier version from
 * {prose date}" line.
 *
 * Delete = TRUE deletion (0141): the row and the stored file both go,
 * for everyone. The row button reads "Delete"; the confirm names the
 * file ("This deletes “{name}” for everyone. It cannot be undone."),
 * offers a quiet download-a-copy-first affordance wired to the existing
 * download grant, and its destructive button ("Delete document") keeps
 * the danger (pepper) treatment the delete-task confirm uses.
 *
 * Removed earlier (the pre-0141 trove): documents archived before true
 * deletion existed are invisible on the tab, so a quiet reveal ("N
 * removed earlier · Review") lists them with two actions each — Delete
 * permanently / Restore. Each row drains through a human decision;
 * there is deliberately no bulk delete. Zero archived rows = the
 * affordance does not render.
 *
 * Design law (UX sweep 2026-07-10, founder-ordered): document KINDS wear
 * identity hues so the filing cabinet reads at a glance — the same move
 * as the clinical identity set (hue as identity, not status): clinical
 * work products (assessment, medical record) take the sage service
 * family; legal instruments and intake paperwork take the
 * warm specialty family (the official/seal gold identity, NOT
 * gold-soft, which stays awaiting-confirm only); insurance takes the
 * coverage green the Medicaid chips anchored; correspondence and other
 * stay neutral facts.
 * Actions take ONE weight ladder per row: the lead affordance (Open, or
 * Download when the type has no preview) is the row's one primary;
 * every other action is a quiet ghost. Uploader renders by display name
 * (resolved server-side) — never a raw uuid. Dates are the record-wide
 * prose treatment (TodayContext).
 */

import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';
import { canUseAiTools, canWriteClinicalNotes, canWriteClinicalRecord } from '@/lib/capabilities';
import { usePhotoIntakeDoor } from '@/lib/aiToolToggles';
import { ErrorNotice } from '@/components/ErrorNotice';
import { RowEditDialog } from '@/components/clientRecord/RowEditDialog';
import {
  DOCUMENT_KIND_LABELS,
  DOCUMENT_KIND_OPTIONS,
  DOCUMENT_ACCEPT,
  clientDocumentsArchivedKey,
  clientDocumentsKey,
  documentsApi,
  isPreviewable,
  resolveContentType,
  splitEnding,
  withEnding,
  type ArchivedDocumentsListResponse,
  type DocumentKind,
  type DocumentWire,
  type DocumentsListResponse,
} from '@/lib/documents';
// The popup / save-to-disk gestures live in ONE place now (they were
// hand-copied across three surfaces); see src/lib/documentGrants.ts for
// why each one is written the counter-intuitive way it is.
import { openBlankViewerTab, pointViewerAt, saveGrantToDisk } from '@/lib/documentGrants';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip, type ChipVariant } from '@/components/primitives/Chip';
import { Dialog } from '@/components/primitives/Dialog';
import { Field } from '@/components/primitives/Field';
import { FLD } from '@/components/primitives/modalFormStyles';
import { proseDate, proseDayOrDate } from '@/lib/proseDate';
import { practiceDayOfStamp } from '@/lib/practiceDate';
import { useToday } from '@/lib/today';
import { MedListDraftsBlock } from '@/components/clientRecord/MedListDraftsBlock';
import type { ClientEngagement } from '@/lib/clientRecord';
import { SelectField } from '@/components/primitives/SelectField';

/** An upload's failure line; `{contact_system}` is filled from the
 *  support contact where it shows. */
const UPLOAD_FAILED_COPY = "That file didn't upload. Add it again, and if it fails again, tell {contact_system}.";

/** The ruled retry line for a failed download or open (house failure copy). */
const FETCH_FAILED_COPY =
  "That file didn't open. Try again, and if it happens again, tell {contact_system}.";

/**
 * Document-kind identity hues (UX sweep 2026-07-10, founder-ordered
 * "color contrast between the different types of documents"). Identity,
 * not status — so only the identity families appear here: sage (service)
 * for clinical work products, warm (specialty) for the legal family,
 * the coverage green (affirm — the Medicaid-chip anchor) for insurance,
 * sand for ambient kinds. The status registers stay reserved: gold-soft
 * means awaiting-confirm and pepper means attention, so neither may
 * name a document kind.
 */
const DOCUMENT_KIND_CHIP: Record<DocumentKind, ChipVariant> = {
  assessment_delivered: 'service',
  medical_record: 'service',
  legal_instrument: 'specialty',
  intake: 'specialty',
  insurance: 'affirm',
  correspondence: 'neutral',
  other: 'neutral',
};

/** Entry date: 'Today' / 'Yesterday' via TodayContext, prose beyond. */
function formatDate(iso: string, today?: string): string {
  const day = practiceDayOfStamp(iso);
  if (day === null) return iso;
  const s = proseDayOrDate(day, today);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The version line's absolute prose date ('June 1, 2026'). */
function formatVersionDate(iso: string): string {
  const day = practiceDayOfStamp(iso);
  return day === null ? iso : proseDate(day);
}

/** Quiet size label (a fact for the meta line). */
export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export interface DocumentsTabProps {
  clientId: string;
  /** clients.engagement. The tab's intro line, the one place a SPICE-only
   *  client's paperwork read differently, was cut in LP-31 (the heading
   *  says it), so the tab reads the same for every client today. */
  engagement?: ClientEngagement;
}

interface PickedUpload {
  file: File;
  kind: DocumentKind;
  /** The name it is filed under; absent, the file's own name (a Replace). */
  displayName?: string;
  /** Set = this upload replaces that document (version chain). */
  supersedesId: string | null;
}

/** A picked file waiting in the upload box for its name and kind. */
interface PendingFile {
  /** Stable across the list's changes (the row's React key). */
  key: number;
  file: File;
  /** The Name box: the file name without its ending, then whatever is typed. */
  name: string;
  /** The file's ending (".pdf"), kept on the name that is sent. */
  ending: string;
  /** What it files as; '' is "Choose a kind". */
  kind: DocumentKind | '';
  /** True when the row opened with nothing chosen, so the File as help
   *  ("nothing is chosen for you") is true. */
  showKindHelp: boolean;
  /** "Upload" was pressed while this row had no kind. */
  kindError: boolean;
  status: 'waiting' | 'uploading' | 'failed';
}

/** A row's element id: the first row keeps the single-file ids. */
function rowElementId(base: string, index: number): string {
  return index === 0 ? base : `${base}-${index}`;
}

/** The server refuses a name over this many characters. */
const DISPLAY_NAME_MAX = 300;

/** A deep link's ?kind= names the exact instrument (e.g. "polst"), which
 *  is finer-grained than the Documents filing vocabulary — every legal
 *  instrument files as the one 'legal_instrument' kind. A value that IS
 *  already a filing kind passes through; anything else recognized as a
 *  request at all (kind param present, just not one of ours) buckets
 *  there too, since Legal & wishes is the one caller today. No param and
 *  'other' both mean nothing chosen: Other stays in the list and is never
 *  chosen for you. */
function presetDocumentKind(param: string | null): DocumentKind | '' {
  if (param === null || param === 'other') return '';
  const known = DOCUMENT_KIND_OPTIONS.find((o) => o.value === param);
  return known ? known.value : 'legal_instrument';
}

/** The images the photo reader takes from a filed document. */
const CARD_IMAGE_TYPES: ReadonlySet<string> = new Set(['image/jpeg', 'image/png']);

export function DocumentsTab({ clientId }: DocumentsTabProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  const queryClient = useQueryClient();
  const today = useToday();
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });
  const canFile = canWriteClinicalNotes(whoami);
  // "Read into Insurance" is Add insurance's photo door reached from here:
  // the same gate (a seat that keeps the record and uses the AI tools, the
  // tenant's photo reader on).
  const canReadCard = usePhotoIntakeDoor(tenantSlug, canWriteClinicalRecord(whoami) && canUseAiTools(whoami));
  /** The image handed to Add insurance as the card's front. */
  const [cardFront, setCardFront] = useState<File | null>(null);
  const [searchParams] = useSearchParams();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const doorRef = useRef<HTMLButtonElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  /** Each row's File as, by row key, so a press can focus the first
   *  row with no kind. */
  const kindSelectRefs = useRef(new Map<number, HTMLSelectElement>());
  const nextRowKey = useRef(0);
  /** The kind a new row starts on; '' is "Choose a kind". A preset from
   *  ?kind= holds until an upload succeeds. */
  const [kind, setKind] = useState<DocumentKind | ''>(() =>
    presetDocumentKind(searchParams.get('kind')),
  );
  /** The files in the upload box, each waiting for "Upload document". */
  const [pending, setPending] = useState<PendingFile[]>([]);
  /** Set by Cancel so focus goes back to the door once it is on screen. */
  const focusDoorRef = useRef(false);
  /** Non-null while the picker is open FOR a replace action. */
  const [replaceTarget, setReplaceTarget] = useState<DocumentWire | null>(null);
  /** The last attempted Replace, retained so retry is one click. */
  const [lastAttempt, setLastAttempt] = useState<PickedUpload | null>(null);
  const [pickError, setPickError] = useState(false);
  /** The document the grave confirm is open for (fromReview = a removed-earlier row). */
  const [removeTarget, setRemoveTarget] = useState<{
    doc: DocumentWire;
    fromReview: boolean;
  } | null>(null);
  /** The removed-earlier review, revealed on demand. */
  const [reviewOpen, setReviewOpen] = useState(false);

  const queryKey = clientDocumentsKey(tenantSlug, clientId);
  const archivedQueryKey = clientDocumentsArchivedKey(tenantSlug, clientId);
  const { data, isPending, isError, error, refetch } = useQuery<DocumentsListResponse>({
    queryKey,
    queryFn: ({ signal }) => documentsApi.list(tenantSlug, clientId, signal),
  });
  const rows = data?.rows ?? [];
  const archivedCount = data?.archived_count ?? 0;

  // The review list is an audited read — fetched only when a human
  // opens the reveal, never as a side effect of rendering the tab.
  const showReview = canFile && archivedCount > 0;
  const archivedQuery = useQuery<ArchivedDocumentsListResponse>({
    queryKey: archivedQueryKey,
    queryFn: ({ signal }) => documentsApi.listArchived(tenantSlug, clientId, signal),
    enabled: showReview && reviewOpen,
  });
  const archivedRows = archivedQuery.data?.rows ?? [];

  const uploadMut = useMutation({
    mutationFn: (attempt: PickedUpload) =>
      documentsApi.uploadDocument(tenantSlug, clientId, {
        kind: attempt.kind,
        file: attempt.file,
        display_name: attempt.displayName,
        supersedes_document_id: attempt.supersedesId,
      }),
    // Replace only: a new file uploads from its own row (uploadRow).
    onSuccess: () => {
      setLastAttempt(null);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  function updateRow(key: number, change: Partial<PendingFile>): void {
    setPending((prev) => prev.map((p) => (p.key === key ? { ...p, ...change } : p)));
  }

  /** One row's upload: on landing it leaves the list and the ?kind=
   *  preset lets go; on a failure it stays, with Try again. */
  async function uploadRow(row: PendingFile): Promise<void> {
    if (row.kind === '') return;
    updateRow(row.key, { status: 'uploading', kindError: false });
    try {
      await documentsApi.uploadDocument(tenantSlug, clientId, {
        kind: row.kind,
        file: row.file,
        display_name: withEnding(row.name, row.ending, row.file.name),
        supersedes_document_id: null,
      });
      setPending((prev) => prev.filter((p) => p.key !== row.key));
      setKind('');
      void queryClient.invalidateQueries({ queryKey });
    } catch {
      updateRow(row.key, { status: 'failed' });
    }
  }

  // True deletion (0141): row first server-side, stored file second.
  const removeMut = useMutation({
    mutationFn: (documentId: string) => documentsApi.deleteDocument(tenantSlug, documentId),
    onSuccess: () => {
      setRemoveTarget(null);
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: archivedQueryKey });
    },
  });

  // Restore a removed-earlier row back onto the tab.
  const restoreMut = useMutation({
    mutationFn: (documentId: string) => documentsApi.setArchived(tenantSlug, documentId, false),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: archivedQueryKey });
    },
  });

  const downloadMut = useMutation({
    mutationFn: (documentId: string) => documentsApi.downloadGrant(tenantSlug, documentId),
    onSuccess: (grant) => {
      saveGrantToDisk(grant.download_url);
    },
  });

  const openMut = useMutation({
    mutationFn: (vars: { documentId: string; viewer: Window | null }) =>
      documentsApi.downloadGrant(tenantSlug, vars.documentId, 'inline'),
    onSuccess: (grant, vars) => {
      pointViewerAt(vars.viewer, grant.download_url);
    },
    onError: (_err, vars) => {
      // Don't leave a stray blank tab behind a failed grant.
      vars.viewer?.close();
    },
  });

  // The image's bytes come through the inline grant and stay in memory:
  // nothing is filed or downloaded to the device.
  const readCardMut = useMutation({
    mutationFn: async (doc: DocumentWire) => {
      const blob = await documentsApi.getFileBytes(tenantSlug, doc.id);
      return new File([blob], doc.display_name, { type: doc.content_type });
    },
    onSuccess: (file) => setCardFront(file),
  });

  function openDocument(documentId: string): void {
    // The tab opens SYNCHRONOUSLY on the click gesture and is pointed at
    // the grant once it lands (see documentGrants.ts).
    openMut.mutate({ documentId, viewer: openBlankViewerTab() });
  }

  /** A Replace uploads at once, under the replaced document's kind and
   *  the file's own name. */
  function startReplace(file: File, supersedes: DocumentWire): void {
    setPickError(false);
    if (resolveContentType(file) === null) {
      setPickError(true);
      return;
    }
    const attempt: PickedUpload = {
      file,
      kind: supersedes.kind,
      supersedesId: supersedes.id,
    };
    setLastAttempt(attempt);
    uploadMut.mutate(attempt);
  }

  /** New files open the box, or join the list in it, one row each;
   *  nothing is sent until "Upload document". A file of a type we cannot
   *  file says so and joins nothing. */
  function addToBox(files: readonly File[]): void {
    const usable = files.filter((f) => resolveContentType(f) !== null);
    setPickError(usable.length < files.length);
    if (usable.length === 0) return;
    uploadMut.reset();
    setLastAttempt(null);
    const rows = usable.map((file): PendingFile => {
      const { name, ending } = splitEnding(file.name);
      return {
        key: nextRowKey.current++,
        file,
        name,
        ending,
        kind,
        showKindHelp: kind === '',
        kindError: false,
        status: 'waiting',
      };
    });
    setPending((prev) => [...prev, ...rows]);
  }

  function onFilePicked(files: FileList | null): void {
    const picked = files ? Array.from(files) : [];
    const target = replaceTarget;
    setReplaceTarget(null);
    if (picked.length === 0) return;
    // Replace stays one file: the first one picked.
    if (target) startReplace(picked[0]!, target);
    else addToBox(picked);
  }

  const rowUploading = pending.some((p) => p.status === 'uploading');
  const uploading = uploadMut.isPending || rowUploading;

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (!canFile || uploading) return;
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length > 0) addToBox(files);
  }

  /** "Upload document": every row with a kind goes, each on its own; a
   *  row with no kind sends nothing and says so, and the first takes
   *  focus. */
  function submitPending(): void {
    const ready = pending.filter((p) => p.status !== 'uploading');
    const missing = ready.filter((p) => p.kind === '');
    if (missing.length > 0) {
      setPending((prev) => prev.map((p) => (p.kind === '' ? { ...p, kindError: true } : p)));
      kindSelectRefs.current.get(missing[0]!.key)?.focus();
    }
    setPickError(false);
    for (const row of ready) if (row.kind !== '') void uploadRow(row);
  }

  function removeRow(key: number): void {
    setPending((prev) => prev.filter((p) => p.key !== key));
  }

  function cancelPending(): void {
    setPending([]);
    setLastAttempt(null);
    uploadMut.reset();
    focusDoorRef.current = true;
  }

  const boxOpen = pending.length > 0;
  // The box's first field takes focus when it opens; the door takes it
  // back when Cancel closes the box (the door is not on screen while the
  // box is, so without this focus would fall to the page).
  useEffect(() => {
    if (boxOpen) {
      nameInputRef.current?.focus();
    } else if (focusDoorRef.current) {
      focusDoorRef.current = false;
      doorRef.current?.focus();
    }
  }, [boxOpen]);

  // The box's own line is the unsupported-type one; a failed new file
  // shows on its row. Outside the box this also carries a failed Replace.
  const showUploadError = !uploadMut.isPending && (uploadMut.isError || pickError);
  const uploadError = showUploadError && (
    <p role="alert" data-testid="documents-upload-error" className="text-xs text-pepper mt-3">
      {pickError
        ? "That kind of file can't be added. A PDF, Word document, image or plain text file will go right in."
        : contactSentence(UPLOAD_FAILED_COPY, contact)}{' '}
      {!pickError && lastAttempt && (
        <Button
          variant="ghost"
          size="sm"
          data-testid="documents-upload-retry"
          onClick={() => uploadMut.mutate(lastAttempt)}
        >
          Try again
        </Button>
      )}
    </p>
  );
  const pickErrorLine = pickError && (
    <p role="alert" data-testid="documents-upload-error" className="text-xs text-pepper mt-3">
      That kind of file can't be added. A PDF, Word document, image or plain text file will go right in.
    </p>
  );

  function renderPendingRow(row: PendingFile, index: number): ReactNode {
    const nameId = rowElementId('documents-name-input', index);
    const kindId = rowElementId('documents-kind-select', index);
    const nameHelp = row.ending === '' ? undefined : `Its ending (${row.ending}) is kept.`;
    const kindDescribedBy =
      [row.showKindHelp ? `${kindId}-help` : null, row.kindError ? `${kindId}-error` : null]
        .filter(Boolean)
        .join(' ') || undefined;
    return (
      <div
        key={row.key}
        data-testid={`documents-pending-row-${index}`}
        className={index === 0 ? '' : 'mt-4 border-t border-sage-800/[8%] pt-4'}
      >
        <p data-testid="documents-pending-file" className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-medium text-ink break-all">{row.file.name}</span>
          <span className="text-ink-soft">{formatSize(row.file.size)}, ready to upload</span>
          {pending.length > 1 && row.status !== 'uploading' && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              data-testid="documents-pending-remove"
              aria-label={`Remove ${row.file.name}`}
              onClick={() => removeRow(row.key)}
            >
              Remove
            </Button>
          )}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-3">
          <Field label="Name" htmlFor={nameId} help={nameHelp}>
            <input
              ref={index === 0 ? nameInputRef : undefined}
              id={nameId}
              data-testid="documents-name-input"
              type="text"
              className={FLD}
              value={row.name}
              maxLength={DISPLAY_NAME_MAX - row.ending.length}
              aria-describedby={nameHelp === undefined ? undefined : `${nameId}-help`}
              onChange={(e) => updateRow(row.key, { name: e.target.value })}
            />
          </Field>
          <Field
            label="File as"
            required
            htmlFor={kindId}
            help={row.showKindHelp ? 'Other is in the list; nothing is chosen for you.' : undefined}
            error={row.kindError ? 'Choose what to file it as.' : undefined}
          >
            <SelectField size="field"
              ref={(el) => {
                if (el) kindSelectRefs.current.set(row.key, el);
                else kindSelectRefs.current.delete(row.key);
              }}
              id={kindId}
              data-testid="documents-kind-select"
              className="w-full"
              value={row.kind}
              aria-required="true"
              aria-invalid={row.kindError ? 'true' : undefined}
              aria-describedby={kindDescribedBy}
              onChange={(e) => updateRow(row.key, { kind: e.target.value as DocumentKind | '', kindError: false })}
            >
              <option value="">Choose a kind</option>
              {DOCUMENT_KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SelectField>
          </Field>
        </div>
        {row.status === 'uploading' && (
          <p data-testid="documents-uploading" className="text-sm text-ink mt-3" role="status">
            Uploading…
          </p>
        )}
        {row.status === 'failed' && (
          <p role="alert" data-testid="documents-upload-error" className="text-xs text-pepper mt-3">
            {contactSentence(UPLOAD_FAILED_COPY, contact)}{' '}
            <Button variant="ghost" size="sm" data-testid="documents-upload-retry" onClick={() => void uploadRow(row)}>
              Try again
            </Button>
          </p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="tab-documents" className="flex flex-col gap-4">
      {/* Medication lists saved for this client (the writing tool's drafts,
          linked to the record) sit beside the filing cabinet. */}
      <MedListDraftsBlock clientId={clientId} />
      <div className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-6">
        <header className="flex items-center gap-2 mb-4">
          <h3 className="font-display text-lg text-sage-800">Documents</h3>
        </header>

        {canFile && (
          <div
            data-testid="documents-upload-zone"
            className="rounded-card border border-dashed border-sage-300 bg-cream/50 px-6 py-6 text-center mb-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <input
              ref={fileInputRef}
              data-testid="documents-upload-input"
              type="file"
              multiple
              accept={DOCUMENT_ACCEPT}
              className="sr-only"
              onChange={(e) => {
                onFilePicked(e.target.files);
                e.target.value = '';
              }}
            />
            {boxOpen ? (
              <>
                <div className="text-left">
                  {pending.map(renderPendingRow)}
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <Button
                      variant="primary"
                      size="sm"
                      data-testid="documents-upload-submit"
                      loading={rowUploading}
                      onClick={submitPending}
                    >
                      {pending.length > 1 ? 'Upload documents' : 'Upload document'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="documents-upload-cancel"
                      disabled={rowUploading}
                      onClick={cancelPending}
                    >
                      Cancel
                    </Button>
                  </div>
                  {pickErrorLine}
                </div>
                <p className="text-sm text-ink-soft mt-4">Or drag more files here.</p>
              </>
            ) : uploadMut.isPending ? (
              <p data-testid="documents-uploading" className="text-sm text-ink" role="status">
                Uploading…
              </p>
            ) : (
              <>
                <p className="text-sm text-ink-soft mb-3">Drag a file here, or</p>
                <Button
                  ref={doorRef}
                  variant="secondary"
                  size="sm"
                  data-testid="documents-upload-button"
                  onClick={() => {
                    setReplaceTarget(null);
                    fileInputRef.current?.click();
                  }}
                >
                  Upload a document
                </Button>
                {uploadError}
              </>
            )}
          </div>
        )}

        <AsyncBoundary what="the documents"
          isLoading={isPending}
          isError={isError}
          error={error}
          onRetry={() => void refetch()}
          // One empty state, not two: the dropzone above already invites
          // the first upload for a viewer who can file one, so this
          // empty-state message reserves itself for the viewer who has no
          // dropzone at all.
          isEmpty={!canFile && data !== undefined && rows.length === 0}
          emptyHeadline="Upload the first document to start this client's file."
        >
          <ul className="flex flex-col divide-y divide-sage-800/[8%]">
            {rows.map((doc) => (
              <li
                key={doc.id}
                data-testid={`document-row-${doc.id}`}
                className="py-3 flex flex-col gap-1"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Chip variant={DOCUMENT_KIND_CHIP[doc.kind] ?? 'neutral'}>
                    {DOCUMENT_KIND_LABELS[doc.kind] ?? 'Other'}
                  </Chip>
                  <span className="text-sm font-medium text-ink break-all">
                    {doc.display_name}
                  </span>
                  <span className="flex-1" />
                  {/* ONE weight ladder per row (weight is importance): the
                      lead affordance — Open, or Download when the type has
                      no preview — is the row's one primary; the rest are
                      quiet ghosts. */}
                  {isPreviewable(doc.content_type) && (
                    <Button
                      variant="primary"
                      size="sm"
                      data-testid={`document-open-${doc.id}`}
                      loading={openMut.isPending && openMut.variables?.documentId === doc.id}
                      onClick={() => openDocument(doc.id)}
                    >
                      Open
                    </Button>
                  )}
                  <Button
                    variant={isPreviewable(doc.content_type) ? 'ghost' : 'primary'}
                    size="sm"
                    data-testid={`document-download-${doc.id}`}
                    loading={downloadMut.isPending && downloadMut.variables === doc.id}
                    onClick={() => downloadMut.mutate(doc.id)}
                  >
                    Download
                  </Button>
                  {canReadCard && CARD_IMAGE_TYPES.has(doc.content_type) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`document-read-insurance-${doc.id}`}
                      loading={readCardMut.isPending && readCardMut.variables?.id === doc.id}
                      onClick={() => readCardMut.mutate(doc)}
                    >
                      Read into Insurance
                    </Button>
                  )}
                  {canFile && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`document-replace-${doc.id}`}
                        // A replacement never runs under a file waiting in
                        // the upload box.
                        disabled={boxOpen}
                        onClick={() => {
                          setReplaceTarget(doc);
                          fileInputRef.current?.click();
                        }}
                      >
                        Replace
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`document-remove-${doc.id}`}
                        onClick={() => setRemoveTarget({ doc, fromReview: false })}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-ink-soft">
                  {doc.uploaded_by_display_name ? `${doc.uploaded_by_display_name} · ` : ''}
                  {formatDate(doc.created_at, today)} · {formatSize(doc.size_bytes)}
                </p>
                {doc.supersedes_created_at && (
                  <p
                    className="text-xs text-ink-soft"
                    data-testid={`document-version-${doc.id}`}
                  >
                    Replaces the earlier version from {formatVersionDate(doc.supersedes_created_at)}.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </AsyncBoundary>

        {downloadMut.isError && (
          <p role="alert" className="text-xs text-pepper mt-3" data-testid="documents-download-error">
            {contactSentence(FETCH_FAILED_COPY, contact)}
          </p>
        )}
        {openMut.isError && (
          <p role="alert" className="text-xs text-pepper mt-3" data-testid="documents-open-error">
            {contactSentence(FETCH_FAILED_COPY, contact)}
          </p>
        )}
        {readCardMut.isError && (
          <div className="mt-3">
            <ErrorNotice
              surface="insurance_card_extract"
              code="document_unavailable"
              data-testid="documents-read-insurance-error"
            />
          </div>
        )}
        {cardFront !== null && (
          <RowEditDialog
            projection="coverage"
            rowId=""
            values={{}}
            mode="create"
            cardPhotoFront={cardFront}
            onClose={() => setCardFront(null)}
          />
        )}

        {/* Removed earlier (the pre-true-delete trove): a quiet reveal.
            Each row leaves through one human decision — restore, or
            delete for good. Drained to zero, this whole block goes. */}
        {showReview && (
          <div className="mt-4 pt-3 border-t border-sage-800/[8%]">
            <Button
              variant="ghost"
              size="sm"
              data-testid="documents-review-toggle"
              onClick={() => setReviewOpen((v) => !v)}
            >
              {archivedCount === 1
                ? '1 removed earlier · Review'
                : `${archivedCount} removed earlier · Review`}
            </Button>
            {reviewOpen && (
              <div className="mt-3" data-testid="documents-review-section">
                <p className="text-sm text-ink-soft mb-2">
                  These were removed back when removing only hid them.
                  Restore anything worth keeping; delete the rest for good.
                </p>
                <AsyncBoundary what="the documents"
                  isLoading={archivedQuery.isPending}
                  isError={archivedQuery.isError}
                  error={archivedQuery.error}
                  onRetry={() => void archivedQuery.refetch()}
                  isEmpty={archivedQuery.data !== undefined && archivedRows.length === 0}
                  emptyHeadline="All reviewed"
                  emptyBody="Nothing removed earlier is waiting here."
                >
                  <ul className="flex flex-col divide-y divide-sage-800/[8%]">
                    {archivedRows.map((doc) => (
                      <li
                        key={doc.id}
                        data-testid={`document-archived-row-${doc.id}`}
                        className="py-3 flex flex-col gap-1"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <Chip variant={DOCUMENT_KIND_CHIP[doc.kind] ?? 'neutral'}>
                            {DOCUMENT_KIND_LABELS[doc.kind] ?? 'Other'}
                          </Chip>
                          <span className="text-sm font-medium text-ink break-all">
                            {doc.display_name}
                          </span>
                          <span className="flex-1" />
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`document-restore-${doc.id}`}
                            loading={restoreMut.isPending && restoreMut.variables === doc.id}
                            onClick={() => restoreMut.mutate(doc.id)}
                          >
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`document-delete-${doc.id}`}
                            onClick={() => setRemoveTarget({ doc, fromReview: true })}
                          >
                            Delete permanently
                          </Button>
                        </div>
                        <p className="text-xs text-ink-soft">
                          {doc.uploaded_by_display_name
                            ? `${doc.uploaded_by_display_name} · `
                            : ''}
                          {doc.archived_at
                            ? `Removed ${formatDate(doc.archived_at, today)} · `
                            : ''}
                          {formatSize(doc.size_bytes)}
                        </p>
                      </li>
                    ))}
                  </ul>
                </AsyncBoundary>
                {restoreMut.isError && (
                  <p
                    role="alert"
                    className="text-xs text-pepper mt-2"
                    data-testid="documents-restore-error"
                  >
                    {contactSentence(
                      "That document didn't come back. Try again, and if it happens again, tell {contact_system}.",
                      contact,
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {removeTarget !== null && (
        <Dialog
          title="Delete this document?"
          role="alertdialog"
          size="sm"
          testId="document-remove-dialog"
          onClose={() => setRemoveTarget(null)}
          footer={
            <>
              <Button size="sm" variant="ghost" onClick={() => setRemoveTarget(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={removeMut.isPending}
                onClick={() => removeMut.mutate(removeTarget.doc.id)}
              >
                Delete document
              </Button>
            </>
          }
        >
          <div className="space-y-3 pb-3">
            <p className="text-sm text-ink-soft">
              This deletes “{removeTarget.doc.display_name}” for everyone. It cannot be undone.
            </p>
            {removeTarget.fromReview ? (
              // A removed-earlier row can't be downloaded while removed;
              // Restore is the way to keep a copy.
              <p className="text-xs text-ink-soft">To keep a copy, restore it first.</p>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                data-testid="document-remove-download-first"
                loading={downloadMut.isPending && downloadMut.variables === removeTarget.doc.id}
                onClick={() => downloadMut.mutate(removeTarget.doc.id)}
              >
                Download a copy first
              </Button>
            )}
            {removeMut.isError && (
              <p role="alert" className="text-xs text-pepper">
                That didn't work. Try again.
              </p>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
